import React from "react";

import Footer from "./Footer";
import RetailerGlobalNavLoggedIn, {
  type RetailerBottomNavLink,
} from "./RetailerGlobalNavLoggedIn";

interface RetailerLayoutProps {
  children: React.ReactNode;
  languageSelector?: boolean;
  cartCount?: number;
  focusedNav?: boolean;
  hideFooter?: boolean;
  /**
   * Remove the Faire global navigation entirely (not just collapse it like
   * `navVisible`) — for previewing a brand surface with no Faire chrome at
   * all, e.g. as a brand-owned storefront. Opt-in; defaults to false so every
   * existing prototype is unaffected.
   */
  hideNav?: boolean;
  hideSearch?: boolean; // Hide the nav search bar
  hideAllCategories?: boolean; // Hide the All categories button and mega-menu
  hideNotifications?: boolean; // Hide the notifications bell in the global nav row
  hideAccountIcon?: boolean; // Hide the account/profile icon in the global nav row
  hideCartIcon?: boolean; // Hide the cart icon in the global nav row
  /** Optional icon button rendered directly to the right of the cart control (e.g. a sidecart toggle). */
  sidecartNavButton?: {
    onClick: () => void;
    isOpen: boolean;
    label?: string;
  };
  signedIn?: boolean; // When false, the global nav shows the logged-out (Sign in / Sign up) actions. Default true.
  hideLanguageSelector?: boolean; // Logged-out nav: hide the EN-US locale button
  hideSellerSignup?: boolean; // Logged-out nav: hide the "Sign up to sell" link
  hideBlogLink?: boolean; // Logged-out nav: hide the "Blog" link
  logoRowHeight?: number; // Fixed height (px) for the logo/actions row, contents centered
  showBottomNavLinks?: boolean; // When set, overrides bottom nav visibility (default: !hideSearch)
  navLinksInLogoRow?: boolean; // Render bottom nav links on the logo row instead of a second row
  bottomNavLinksOverride?: RetailerBottomNavLink[]; // Replace default bottom nav links
  navSearchBar?: React.ReactNode; // Search bar to show in nav
  stickyNav?: boolean; // Whether the nav should be sticky (default: true)
  /** When false, nav is collapsed (e.g. hide on scroll down, show on scroll up). Default true. */
  navVisible?: boolean;
  contentMaxWidth?: string; // Max width for content area (affects nav width too)
  setupGuideButton?: {
    onClick: () => void;
    label?: string;
  }; // Optional setup guide button in nav
  commandCenterButton?: {
    onClick: () => void;
    label?: string;
    isActive?: boolean;
  }; // Optional command center toggle in nav
  compassPanelBehavior?: boolean; // Show compass sidebar icon button
  onCompassClick?: () => void; // Handler for compass button click
  isCompassPanelOpen?: boolean; // Whether the compass panel is currently open
  compassPanelVariant?: number; // Variant: 1 = nav icon, 2 = floating icon, 3 = left of logo
  compassPanel?: React.ReactNode; // The compass panel component
  disableLogoLink?: boolean; // Disable logo link navigation
  defaultSearchValue?: string; // Initial value for search bar
  searchPlaceholder?: string; // Placeholder text for the nav search input
  onSearchSubmit?: (query: string) => void; // Called when user submits a search from nav
  controlledSearchValue?: string; // When set, nav search bar value is controlled externally
  logoLinkUrl?: string; // Custom URL for the logo link (default: "/homepage")
  extraNavLinks?: { label: string; href: string }[]; // Extra links prepended before "Brand updates"
  cartHref?: string; // Cart link URL in nav (default: "/cart")
  /** When set, header cart opens side cart instead of navigating */
  onCartClick?: () => void;
  /** Hides the nav cart icon entirely — for surfaces with a different, dedicated cart entry point (e.g. a persistent global sidecart rail). Default false. */
  hideCart?: boolean;
  onFaireAiClick?: () => void; // Handler for Faire AI button click
  isFaireAiOpen?: boolean; // Whether the Faire AI panel is currently open
  onAccountClick?: () => void; // Handler for account button click (overrides default behavior)
  accountOverdueDot?: boolean; // Show red overdue dot on account icon
  /**
   * Account dropdown anchored under the account glyph (see
   * `RetailerGlobalNavLoggedIn`). Pair with `RetailerAccountMenu` from
   * `components/shared` for the production menu.
   */
  accountDropdown?: (close: () => void) => React.ReactNode;
  /** Hides the account/profile icon entirely — for surfaces with a different, dedicated account entry point (e.g. a persistent right-rail nav). Default false. */
  hideAccount?: boolean;
  allowOverflowX?: boolean;
  /** Optional right column inside the desktop “All categories” mega-menu (e.g. sponsored rail). */
  categoryMenuPromotedColumn?: React.ReactNode;
}

/**
 * Base layout component for retailer pages.
 * Includes the global navigation and footer.
 * Use this as the wrapper for all retailer-facing pages.
 */
export default function RetailerLayout({
  children,
  languageSelector = false,
  cartCount = 0,
  focusedNav = false,
  hideFooter = false,
  hideNav = false,
  hideSearch = false,
  hideAllCategories = false,
  hideNotifications = false,
  hideAccountIcon = false,
  hideCartIcon = false,
  sidecartNavButton,
  signedIn = true,
  hideLanguageSelector = false,
  hideSellerSignup = false,
  hideBlogLink = false,
  logoRowHeight,
  showBottomNavLinks,
  navLinksInLogoRow = false,
  bottomNavLinksOverride,
  navSearchBar = null,
  stickyNav = true,
  navVisible = true,
  contentMaxWidth = "1920px",
  setupGuideButton,
  commandCenterButton,
  compassPanelBehavior = false,
  onCompassClick,
  isCompassPanelOpen = false,
  compassPanelVariant = 1,
  compassPanel,
  disableLogoLink = false,
  defaultSearchValue = "",
  searchPlaceholder,
  onSearchSubmit,
  controlledSearchValue,
  logoLinkUrl = "/homepage",
  extraNavLinks = [],
  cartHref = "/cart",
  onCartClick,
  hideCart = false,
  onFaireAiClick,
  isFaireAiOpen = false,
  onAccountClick,
  accountOverdueDot,
  accountDropdown,
  hideAccount = false,
  allowOverflowX,
  categoryMenuPromotedColumn,
}: RetailerLayoutProps) {
  const isVariant3 = compassPanelVariant === 3;
  const isVariant3b = compassPanelVariant === 5;
  const isLeftPanel = (isVariant3 || isVariant3b) && isCompassPanelOpen;

  return (
    <div
      className="bg-fs-surface-primary min-h-screen"
      style={{
        width: "100%",
        // `clip`, not `hidden`: per CSS, setting one axis to `hidden` forces
        // the other axis to compute to `auto`, which turns this whole-page
        // wrapper into a second scroll container nested inside the document's
        // — the classic double-scrollbar setup. `clip` still clips horizontal
        // overflow but never creates a scrollport, so the page has exactly one
        // scrollbar and sticky descendants stick to the viewport.
        overflowX: allowOverflowX ? "visible" : "clip",
      }}
    >
      {compassPanel}
      <div
        className="flex flex-col"
        style={{
          width: isCompassPanelOpen ? "75%" : "100%",
          marginLeft: isCompassPanelOpen && isLeftPanel ? "25%" : "0",
          marginRight: isCompassPanelOpen && !isLeftPanel ? "25%" : "0",
          transition:
            "width 0.35s cubic-bezier(0.4, 0, 0.2, 1), margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1), margin-right 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            // Let the All categories mega-menu and backdrop extend past the first nav row
            // (a small max-height + overflow:hidden was clipping the entire dropdown).
            overflow: navVisible ? "visible" : "hidden",
            maxHeight: navVisible ? 2200 : 0,
            transition: "max-height 0.3s ease-out",
            flexShrink: 0,
            zIndex: 600,
            display: hideNav ? "none" : undefined,
          }}
        >
          <RetailerGlobalNavLoggedIn
            languageSelector={languageSelector}
            cartCount={cartCount}
            focused={focusedNav}
            hideSearch={hideSearch}
            hideAllCategories={hideAllCategories}
            hideNotifications={hideNotifications}
            hideAccountIcon={hideAccountIcon}
            hideCartIcon={hideCartIcon}
            sidecartNavButton={sidecartNavButton}
            signedIn={signedIn}
            hideLanguageSelector={hideLanguageSelector}
            hideSellerSignup={hideSellerSignup}
            hideBlogLink={hideBlogLink}
            logoRowHeight={logoRowHeight}
            showBottomNavLinks={showBottomNavLinks}
            navLinksInLogoRow={navLinksInLogoRow}
            bottomNavLinksOverride={bottomNavLinksOverride}
            navSearchBar={navSearchBar}
            sticky={stickyNav}
            contentMaxWidth={contentMaxWidth}
            setupGuideButton={setupGuideButton}
            commandCenterButton={commandCenterButton}
            compassPanelBehavior={compassPanelBehavior}
            onCompassClick={onCompassClick}
            isCompassPanelOpen={isCompassPanelOpen}
            compassPanelVariant={compassPanelVariant}
            disableLogoLink={disableLogoLink}
            defaultSearchValue={defaultSearchValue}
            searchPlaceholder={searchPlaceholder}
            onSearchSubmit={onSearchSubmit}
            controlledSearchValue={controlledSearchValue}
            logoLinkUrl={logoLinkUrl}
            extraNavLinks={extraNavLinks}
            cartHref={cartHref}
            onCartClick={onCartClick}
            hideCart={hideCart}
            onFaireAiClick={onFaireAiClick}
            isFaireAiOpen={isFaireAiOpen}
            onAccountClick={onAccountClick}
            accountOverdueDot={accountOverdueDot}
            accountDropdown={accountDropdown}
            hideAccount={hideAccount}
            categoryMenuPromotedColumn={categoryMenuPromotedColumn}
          />
        </div>
        <main
          className="flex-1"
          style={{
            paddingBottom: hideFooter ? 0 : "48px",
            height: "fit-content",
            // Stay below the nav + mega-menu stacking context so the open menu isn’t covered.
            position: "relative",
            zIndex: 0,
          }}
        >
          {children}
        </main>
        {!hideFooter && <Footer />}
      </div>
    </div>
  );
}
